import { ValidatorFn, AbstractControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { formatNumber } from '@angular/common';
import dayjs from 'dayjs';

export interface NumberFormatOptions {
    needToFormat: boolean;
    locale: string;
    digitsInfo: string;
}

export class NovaValidators {
    static noWhiteSpace(control: AbstractControl): ValidationErrors | null {
        const isWhitespace = ((control && control.value && control.value.toString()) || '').trim().length === 0;
        const isValid = !isWhitespace;
        return isValid ? null : { whitespace: true };
    }

    /**
     * Validator that checks if the control's value has leading or trailing whitespace.
     * Allows all characters in the middle, but disallows leading/trailing spaces, tabs, etc.
     * @param control The form control to validate
     * @returns Validation error if the value has leading/trailing whitespace, otherwise null
     */
    static noLeadingTrailingWhitespace(control: AbstractControl): ValidationErrors | null {
        const value = control?.value?.toString();
        if (!value) {
            return null;
        }
        const hasLeadingTrailingWhitespace = value !== value.trim();
        return hasLeadingTrailingWhitespace ? { leadingTrailingWhitespace: true } : null;
    }

    /**
     * Validator that checks that a physical unit name contains only allowed characters.
     * Allowed: letters, numbers, space, comma, period, slash, dash, apostrophe, parentheses, ampersand.
     */
    static physicalUnitNameAllowedCharacters(control: AbstractControl): ValidationErrors | null {
        const value = control?.value?.toString();
        if (!value) {
            return null;
        }

        const allowedCharactersRegex = /^[A-Za-z0-9 ,.\-\/()'&]*$/;
        return allowedCharactersRegex.test(value) ? null : { invalidCharacters: true };
    }

    /**
     * Validator that disallows ALL CAPS names longer than a certain length.
     * Acronyms are allowed up to the provided length.
     */
    static noAllCapsLongerThan(maxAllowedLength: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control?.value?.toString();
            if (!value) {
                return null;
            }

            if (value.length <= maxAllowedLength) {
                return null;
            }

            // Only flag as ALL CAPS if there is at least one letter.
            const hasLetter = /[A-Za-z]/.test(value);
            if (!hasLetter) {
                return null;
            }

            const isAllCaps = value === value.toUpperCase();
            return isAllCaps ? { allCaps: true } : null;
        };
    }

    static numberDefaultChanged(initialValue: any, options?: NumberFormatOptions): ValidatorFn {
        const defaultValue =
            options && options.needToFormat
                ? formatNumber(initialValue, options.locale, options.digitsInfo)
                : initialValue;

        return (control: AbstractControl): { [key: string]: any } | null => {
            const ctrlValue =
                options && options.needToFormat
                    ? formatNumber(this._parseNumber(control.value), options.locale, options.digitsInfo)
                    : control.value;

            return this._getError(defaultValue, ctrlValue);
        };
    }

    static defaultChanged(initialValue: any): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            return this._getError(initialValue, control.value);
        };
    }

    static numeric(control: AbstractControl) {
        const val = control.value;

        if (val === null || val === '' || val === undefined) {
            return null;
        }

        if (!val.toString().match(/^[0-9]+(\.?[0-9]+)?$/)) {
            return { invalidNumber: true };
        }

        return null;
    }

    static dateDefaultChanged(initialValue: any): ValidatorFn {
        const defaultValue = dayjs(initialValue).format('MM/DD/YYYY');

        return (control: AbstractControl): { [key: string]: any } | null => {
            const ctrlValue = dayjs(control.value).format('MM/DD/YYYY');

            return this._getError(defaultValue, ctrlValue);
        };
    }

    /**
     * Cross field validation; compares start date and end date and sets an error if end date < start date.
     * @param startDate
     * @param endDate
     * @param perpetual - send this parameter in to opt out of the comparison.
     */
    static compareStartEndDates(startDate: string, endDate: string, perpetual?: string): ValidatorFn {
        return (group: UntypedFormGroup): { [key: string]: any } | null => {
            const startDateControl = group.controls[startDate];
            const endDateControl = group.controls[endDate];
            const perpetualControl = perpetual ? group.controls[perpetual] : null;
            // if perpetual=checked/true, then no need to compare dates
            if (perpetualControl && perpetualControl.value) {
                return null;
            }

            if (!endDateControl.value || !startDateControl.value) {
                return null;
            }

            if (new Date(endDateControl.value) < new Date(startDateControl.value)) {
                return { invalidDate: true };
            } else {
                return null;
            }
        };
    }

    /**
     * Cross field validation for radio control and DatePicker control.
     * When a radio button=checked/true, then the Date Picker needs to have a date.
     * @param radio
     * @param datePicker
     */
    static dateNeededWhenRadioIsTrue(radio: string, datePicker: string): ValidatorFn {
        return (group: UntypedFormGroup): { [key: string]: any } | null => {
            const radioControl = group.controls[radio];
            const datePickerControl = group.controls[datePicker];
            const dateValue = Array.isArray(datePickerControl.value) ? null : datePickerControl.value;
            if (radioControl.value && dateValue === null) {
                // setting error here allows message to show up at the "datePickerControl" element
                datePickerControl.setErrors({ missingDate: true });
                return { missingDate: true };
            }

            if (!radioControl.value) {
                datePickerControl.setErrors(undefined);
            }

            return null;
        };
    }

    /**
     * Validates phone number.
     *  matches:
                123-456-7890
                (123) 456-7890
                123 456 7890
                123.456.7890
                +91 (123) 456-7890
        https://stackoverflow.com/questions/16699007/regular-expression-to-match-standard-10-digit-phone-number
     *
     */
    static phoneNumber(control: AbstractControl) {
        // [Ivan Yurchenko]: We should have a Required validator separately if we need. Without this fix it was erroring out
        if (!control || !control.value) {
            return null;
        }

        const phoneNumberRegExp = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
        // remove spaces
        const value: string = control.value.replace(/\s+/g, '');
        if (!value) {
            return null;
        }
        if (!phoneNumberRegExp.test(value)) {
            return {
                wrongFormat: true
            };
        }
        return null;
    }

    /**
     * Validates US zip code for 5 or 9 digits.  Matches:
     * 38980 and 83900-8789
     * https://stackoverflow.com/questions/1435435/javascript-regular-expression-to-match-5-or-9-digit-zipcode
     */
    static usZipCode(control: AbstractControl) {
        const zipCodeRegex = /^\d{5}$|^\d{5}-\d{4}$/;
        return NovaValidators.validateZipCode(control, zipCodeRegex);
    }

    /**
     * Validates zip code.
     * Matches:
     "12345" (US),
     "12345-6789" (US+4),
     "K1A 0B1" (CA with a space),
     "K1A0B1" (CA without a space)
     *
     */
    static usOrCanadaZipCode(control: AbstractControl) {
        const zipCodeRegex = /^(?:\d{5}(?:-\d{4})?|[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)$/;
        return NovaValidators.validateZipCode(control, zipCodeRegex);
    }

    /**
     * Validates short US zip code - 5 digits.
     * @param control The control whose value needs to be validated
     */
    static shortZipCode(control: AbstractControl) {
        return NovaValidators.validateZipCode(control, /^\d{5}$/);
    }

    private static validateZipCode(control: AbstractControl, regex: RegExp) {
        const value: string = control?.value?.toString().trim();
        if (!value) {
            return null;
        }
        if (!regex.test(value)) {
            return {
                wrongFormat: true
            };
        }
        return null;
    }

    static dateRange(minDate: Date | null, maxDate: Date | null): ValidatorFn {
        return (c: AbstractControl): ValidationErrors | null => {
            const validationError = { dateRange: true };

            if (!c.value) {
                return null;
            }

            const timestamp = Date.parse(c.value);
            if (isNaN(timestamp)) {
                return validationError;
            }

            const date = new Date(timestamp);
            if ((minDate && minDate > date) || (maxDate && maxDate < date)) {
                return validationError;
            }

            return null;
        };
    }

    static minDate(minDate: Date | null): ValidatorFn {
        return (c: AbstractControl): ValidationErrors | null => {
            const validationError = { minDate: true };

            if (!c.value) {
                return null;
            }

            const timestamp = Date.parse(c.value);
            if (isNaN(timestamp)) {
                return validationError;
            }

            const date = new Date(timestamp);
            if (minDate && minDate > date) {
                return validationError;
            }

            return null;
        };
    }

    static maxDate(maxDate: Date | null): ValidatorFn {
        return (c: AbstractControl): ValidationErrors | null => {
            const validationError = { maxDate: true };

            if (!c.value) {
                return null;
            }

            const timestamp = Date.parse(c.value);
            if (isNaN(timestamp)) {
                return validationError;
            }

            const date = new Date(timestamp);
            if (maxDate && maxDate < date) {
                return validationError;
            }

            return null;
        };
    }

    private static _parseNumber(value: any): number {
        if (value && value.toString().includes(',')) {
            return Number(value.toString().replace(/,/g, ''));
        } else {
            return Number(value);
        }
    }

    private static _getError(defaultValue: any, ctrlValue: any): { [key: string]: any } | null {
        return JSON.stringify(defaultValue) !== JSON.stringify(ctrlValue)
            ? { defaultChanged: { default: defaultValue, currentValue: ctrlValue } }
            : null;
    }

    static startIsLessThanEnd(start: string, end: string): ValidatorFn {
        return (group: UntypedFormGroup): { [key: string]: any } | null => {
            const firstControl = group.controls[start];
            const secondControl = group.controls[end];

            if (!firstControl.value || !secondControl.value) {
                return null;
            }

            if (secondControl.value < firstControl.value) {
                firstControl.setErrors({ invalidRange: true });
                return { invalidRange: true };
            } else {
                firstControl.setErrors(undefined);
                return null;
            }
        };
    }

    public static website(control: AbstractControl) {
        const value: string = control?.value?.toString().trim();
        if (!value) {
            return null;
        }

        const websiteRegex = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/;
        if (!websiteRegex.test(value)) {
            return {
                wrongFormat: true
            };
        }
        return null;
    }

    public static email(control: AbstractControl) {
        const value: string = control?.value?.toString().trim();
        if (!value) {
            return null;
        }

        const emailRegex =
            /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (!emailRegex.test(value)) {
            return {
                wrongFormat: true
            };
        }
        return null;
    }

    public static labValidator(control: AbstractControl) {
        const value: string = control?.value?.toString().trim();
        if (!value) {
            return null;
        }

        const labRegex = /^(Quest |LabCorp )[0-9]+$/;
        if (!labRegex.test(value)) {
            return {
                wrongFormat: true
            };
        }
        return null;
    }

    /**
     * @description
     * Validator that requires the control's value to be false. Useful when
     * cascading dropdown loading state is stored within a form field.
     *
     * @usageNotes
     *
     * ### Validate that the field is false
     *
     * ```ts
     * const control = new FormControl(true, NovaValidators.requiredFalse);
     *
     * console.log(control.errors); // { requiredFalse: true }
     * ```
     *
     * @returns An error map that contains the `requiredFalse` property
     * set to `true` if the validation check fails, otherwise `null`.
     */
    public static requiredFalse(control: AbstractControl): ValidationErrors | null {
        return requiredFalseValidator(control);
    }
}

/**
 * Validator that requires the control's value to be false.
 */
export function requiredFalseValidator(control: AbstractControl): ValidationErrors | null {
    return control.value === false ? null : { requiredFalse: true };
}
